import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { 
  Dialog, 
  DialogTitle, 
  Box, 
  LinearProgress, 
  Typography, 
  Toolbar,
  Popover,
  useMediaQuery
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import { updatePdfPageDisplaySizes } from './pdfViewerLayout';
import useWheelZoom from './useWheelZoom';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { CloseButton, UnifiedAnswerRenderer } from '../Common';
import {
  VIEWER_TAG_HOVER_EVENT,
  buildTagHoverEventDetail,
  doesTagHoverDetailMatch,
  getTagHighlightBackgroundColor,
  getTagHighlightColor,
  padNormalizedBbox,
  rotateNormalizedBbox,
} from './TagHighlightUtils';
import { useViewerFile } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';
const PDF_CANVAS_SCALE = 1.15;
const PDF_ROTATION_STEP_DEGREES = 90;
const EXPECTED_PDF_RENDER_CANCELLATION_MESSAGES = [
  'Worker was terminated',
  'Worker was destroyed',
  'Page was destroyed',
  'Rendering cancelled',
];

GlobalWorkerOptions.workerSrc = pdfjsWorker;

const normalizeCoordinateItems = (coordinates) => {
  if (!coordinates) {
    return [];
  }

  let parsedCoordinates = coordinates;
  if (typeof coordinates === 'string') {
    try {
      parsedCoordinates = JSON.parse(coordinates);
    } catch (error) {
      return [];
    }
  }

  return Array.isArray(parsedCoordinates) ? parsedCoordinates : [parsedCoordinates];
};

const normalizeTagHighlightBoxes = (timeevent) => {
  const fields = Array.isArray(timeevent?.customFields) ? timeevent.customFields : [];
  const boxes = [];

  fields.forEach((field) => {
    const coordinateItems = normalizeCoordinateItems(field?.coordinates);

    coordinateItems.forEach((coordinates) => {
      const bbox = coordinates?.bbox_percent;
      const page = Number(coordinates?.page || 1);
      if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page) || page < 1) {
        return;
      }

      const normalizedBbox = bbox.map((value) => Number(value));
      if (normalizedBbox.some((value) => !Number.isFinite(value))) {
        return;
      }

      boxes.push({
        page,
        bbox: padNormalizedBbox(normalizedBbox) || normalizedBbox,
        label: field?.variable || '',
        field,
      });
    });
  });

  return boxes;
};

const normalizeTagPopupCoordinates = (field) => normalizeCoordinateItems(field?.coordinates)
  .map((item) => {
    const bbox = item?.bbox_percent;
    const page = Number(item?.page || 1);
    if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page)) {
      return null;
    }

    const normalizedBbox = bbox.map((value) => Number(value));
    if (normalizedBbox.some((value) => !Number.isFinite(value))) {
      return null;
    }

    return {
      page,
      bbox: normalizedBbox,
      text: item?.text || '',
    };
  })
  .filter(Boolean);

const formatCoordinateNumber = (value) => Number(value).toFixed(4);

const isExpectedPdfRenderCancellation = (error) => {
  const message = error?.message || String(error || '');

  return error?.name === 'AbortException' ||
    error?.name === 'RenderingCancelledException' ||
    EXPECTED_PDF_RENDER_CANCELLATION_MESSAGES.some((expectedMessage) => message.includes(expectedMessage));
};

const warnUnexpectedPdfLifecycleError = (message, error) => {
  if (!isExpectedPdfRenderCancellation(error)) {
    console.warn(message, error);
  }
};

const setTagHighlightState = (element, tagColor, { active = false, hidden = false } = {}) => {
  if (!element) {
    return;
  }

  element.style.opacity = hidden ? '0' : (active ? '0.22' : '1');
  element.style.boxShadow = active && !hidden
    ? `0 0 0 3px ${tagColor}, 0 0 0 1px rgba(255,255,255,0.85) inset`
    : 'none';
  element.style.pointerEvents = hidden ? 'none' : 'auto';
};

const updateHoveredTagHighlights = (container, detail, scrollToMatch = false) => {
  if (!container) {
    return;
  }

  const highlights = Array.from(container.querySelectorAll('[data-tag-highlight="true"]'))
    .map((element) => {
      const tag = {
        id: element.dataset.tagId || null,
        timeeventid: element.dataset.eventId || null,
        variable: element.dataset.tagVariable || '',
      };
      const tagColor = element.dataset.tagColor || getTagHighlightColor(tag.variable);

      return {
        element,
        tagColor,
        matches: doesTagHoverDetailMatch(detail, tag),
      };
    });

  const hasHoveredMatch = Boolean(detail) && highlights.some((highlight) => highlight.matches);
  const shouldHideAllHighlights = Boolean(detail) && !hasHoveredMatch;

  highlights.forEach(({ element, tagColor, matches }) => {
    setTagHighlightState(element, tagColor, {
      active: hasHoveredMatch && matches,
      hidden: shouldHideAllHighlights || (hasHoveredMatch && !matches),
    });
  });

  // A tag may belong to another PDF page. Bring its first matching evidence
  // box into view when the user hovers the tag in the validation panel.
  if (scrollToMatch && hasHoveredMatch) {
    const matchingHighlights = highlights.filter((highlight) => highlight.matches);
    const requestedOccurrence = Number(detail?.occurrenceIndex);
    const matchedHighlight = Number.isInteger(requestedOccurrence) && requestedOccurrence >= 0
      ? matchingHighlights[requestedOccurrence] || matchingHighlights[0]
      : matchingHighlights[0];
    matchedHighlight?.element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const PDFContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  overflow: 'auto',
  '& canvas': {
    width: '100%',
    height: 'auto',
    display: 'block',
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginBottom: '16px',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const PDFViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, filePath = '', timeevent = null, onTagHover = null, onTagHoverEnd = null, embedded = false }, _ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    fileBlob: pdfBlob,
    fileUrl: pdfUrl,
    loading,
    progress,
    error,
  } = useViewerFile({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.pdf_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [zoomScale, setZoomScale] = useState(1);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const [pageRenderError, setPageRenderError] = useState(null);
  const [selectedTagPopup, setSelectedTagPopup] = useState(null);
  const containerRef = useRef(null);
  const pagesContainerRef = useRef(null);
  const hoveredTagDetailRef = useRef(null);
  const zoomScaleRef = useRef(zoomScale);
  zoomScaleRef.current = zoomScale;
  const tagHighlightBoxes = React.useMemo(() => normalizeTagHighlightBoxes(timeevent), [timeevent]);
  useWheelZoom({
    containerRef,
    enabled: Boolean(pdfUrl && !loading),
    maximumZoom: 3,
    minimumZoom: 0.5,
    setZoom: setZoomScale,
    zoom: zoomScale,
  });

  useLayoutEffect(() => {
    updatePdfPageDisplaySizes(pagesContainerRef.current, zoomScale);
  }, [zoomScale]);

  useEffect(() => {
    if (!open || !pdfUrl) {
      return undefined;
    }

    const pagesContainer = pagesContainerRef.current;
    const scrollContainer = containerRef.current;
    const updatePageSizes = () => {
      updatePdfPageDisplaySizes(pagesContainer, zoomScaleRef.current);
    };

    updatePageSizes();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updatePageSizes);
      if (pagesContainer) {
        resizeObserver.observe(pagesContainer);
      }
      if (scrollContainer && scrollContainer !== pagesContainer) {
        resizeObserver.observe(scrollContainer);
      }
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', updatePageSizes);
    return () => window.removeEventListener('resize', updatePageSizes);
  }, [open, pdfUrl]);

  useEffect(() => {
    if (typeof window === 'undefined' || !open) {
      return undefined;
    }

    const handleTagHover = (event) => {
      hoveredTagDetailRef.current = event?.detail || null;
      updateHoveredTagHighlights(pagesContainerRef.current, hoveredTagDetailRef.current, true);
    };

    window.addEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
    return () => {
      window.removeEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
      hoveredTagDetailRef.current = null;
    };
  }, [open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setPdfRotation(0);
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      setSelectedTagPopup(null);
    } else {
      setPdfRotation(0);
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      setSelectedTagPopup(null);
    }
  }, [open]);

  useEffect(() => {
    if (!pdfUrl || !open) return;

    let cancelled = false;
    let currentRenderTask = null;
    let loadingTaskDestroyed = false;
    const loadingTask = getDocument({ url: pdfUrl });
    const container = pagesContainerRef.current;

    if (container) {
      container.innerHTML = '';
    }

    setPageRendering(true);
    setPageCount(0);
    setRenderedPages(0);
    setPageRenderError(null);

    const destroyLoadingTask = async () => {
      if (loadingTaskDestroyed) {
        return;
      }

      loadingTaskDestroyed = true;
      try {
        await loadingTask.destroy();
      } catch (destroyError) {
        warnUnexpectedPdfLifecycleError('[PDFViewer] Failed to destroy PDF loading task:', destroyError);
      }
    };

    const renderPdfPages = async () => {
      try {
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNumber);
          try {
            if (cancelled) return;
            const pageRotation = ((Number(page.rotate) || 0) + pdfRotation) % 360;
            const viewport = page.getViewport({ scale: PDF_CANVAS_SCALE, rotation: pageRotation });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            if (!context) {
              throw new Error('Failed to render PDF page');
            }

            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.setAttribute('data-page-number', String(pageNumber));
            canvas.style.display = 'block';
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
            canvas.style.marginBottom = '0';

            if (container) {
              const pageWrapper = document.createElement('div');
              pageWrapper.style.position = 'relative';
              pageWrapper.dataset.pdfPageWrapper = 'true';
              pageWrapper.dataset.pdfNaturalWidth = String(viewport.width);
              pageWrapper.dataset.wheelZoomAnchor = String(pageNumber);
              pageWrapper.style.display = 'block';
              pageWrapper.style.maxWidth = 'none';
              pageWrapper.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
              pageWrapper.style.margin = '0 auto 16px';
              pageWrapper.style.lineHeight = '0';
              pageWrapper.appendChild(canvas);

              const pageBoxes = tagHighlightBoxes.filter((box) => box.page === pageNumber);
              pageBoxes.forEach((box) => {
                const rotatedBbox = rotateNormalizedBbox(box.bbox, pdfRotation);
                if (!rotatedBbox) return;

                const [x0, y0, x1, y1] = rotatedBbox;
                const tagColor = getTagHighlightColor(box.label);
                const tagBackgroundColor = getTagHighlightBackgroundColor(box.label);
                const highlight = document.createElement('div');
                const hoverDetail = buildTagHoverEventDetail(box.field || { variable: box.label }, timeevent);
                highlight.title = box.label;
                highlight.dataset.tagHighlight = 'true';
                highlight.dataset.eventId = hoverDetail?.eventId == null ? '' : String(hoverDetail.eventId);
                highlight.dataset.tagId = hoverDetail?.tagId == null ? '' : String(hoverDetail.tagId);
                highlight.dataset.tagVariable = hoverDetail?.tagVariable || box.label || '';
                highlight.dataset.tagColor = tagColor;
                highlight.style.position = 'absolute';
                highlight.style.left = `${Math.max(0, Math.min(1, x0)) * 100}%`;
                highlight.style.top = `${Math.max(0, Math.min(1, y0)) * 100}%`;
                highlight.style.width = `${Math.max(0, Math.min(1, x1) - Math.max(0, Math.min(1, x0))) * 100}%`;
                highlight.style.height = `${Math.max(0, Math.min(1, y1) - Math.max(0, Math.min(1, y0))) * 100}%`;
                highlight.style.border = `2px solid ${tagColor}`;
                highlight.style.backgroundColor = tagBackgroundColor;
                highlight.style.boxSizing = 'border-box';
                highlight.style.pointerEvents = 'auto';
                highlight.style.cursor = box.field ? 'pointer' : 'default';
                highlight.style.transition = 'opacity 120ms ease, box-shadow 120ms ease';
                highlight.setAttribute('role', box.field ? 'button' : 'presentation');
                if (box.field) {
                  highlight.setAttribute('tabindex', '0');
                  highlight.setAttribute('aria-label', `${t('timeevent.open_tag', 'Open tag')} ${box.label || ''}`.trim());
                }
                highlight.addEventListener('mouseenter', () => {
                  setTagHighlightState(highlight, tagColor, { active: true });
                  if (onTagHover) {
                    onTagHover(box.field);
                  }
                });
                highlight.addEventListener('mouseleave', () => {
                  setTagHighlightState(highlight, tagColor);
                  if (onTagHoverEnd) {
                    onTagHoverEnd();
                  }
                });
                if (box.field) {
                  const openTag = (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedTagPopup({
                      anchorEl: highlight,
                      field: box.field,
                    });
                  };
                  highlight.addEventListener('click', openTag);
                  highlight.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      openTag(event);
                    }
                  });
                }

                if (box.label) {
                  const label = document.createElement('div');
                  label.textContent = box.label;
                  label.style.position = 'absolute';
                  label.style.top = '-2px';
                  label.style.right = '-2px';
                  label.style.maxWidth = '160px';
                  label.style.overflow = 'hidden';
                  label.style.textOverflow = 'ellipsis';
                  label.style.whiteSpace = 'nowrap';
                  label.style.backgroundColor = tagColor;
                  label.style.color = '#fff';
                  label.style.fontSize = '10px';
                  label.style.fontWeight = '700';
                  label.style.lineHeight = '14px';
                  label.style.padding = '1px 5px';
                  label.style.borderRadius = '0 0 0 3px';
                  label.style.boxSizing = 'border-box';
                  label.style.pointerEvents = 'none';
                  highlight.appendChild(label);
                }

                pageWrapper.appendChild(highlight);
              });

              container.appendChild(pageWrapper);
              updatePdfPageDisplaySizes(container, zoomScaleRef.current);
              updateHoveredTagHighlights(container, hoveredTagDetailRef.current);
            }

            currentRenderTask = page.render({ canvasContext: context, viewport });
            await currentRenderTask.promise;
          } finally {
            currentRenderTask = null;
            page.cleanup();
          }

          if (cancelled) return;
          setRenderedPages(pageNumber);
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      } catch (err) {
        if (cancelled || isExpectedPdfRenderCancellation(err)) return;
        console.error('Error rendering PDF pages:', err);
        setPageRenderError(err?.message || 'Failed to render PDF');
      } finally {
        if (!cancelled) {
          setPageRendering(false);
        }
      }
    };

    const renderPromise = renderPdfPages();

    return () => {
      cancelled = true;
      if (currentRenderTask) {
        try {
          currentRenderTask.cancel();
        } catch (cancelError) {
          warnUnexpectedPdfLifecycleError('[PDFViewer] Failed to cancel PDF render task:', cancelError);
        }
      }
      // Let pending PDF.js callbacks settle before terminating the worker. Destroying
      // mid-load can surface expected worker termination as a dev overlay error.
      renderPromise
        .catch((renderError) => {
          warnUnexpectedPdfLifecycleError('[PDFViewer] PDF render task failed during cleanup:', renderError);
        })
        .finally(() => {
          destroyLoadingTask();
        });
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [pdfUrl, open, pdfRotation, tagHighlightBoxes, timeevent, t, onTagHover, onTagHoverEnd]);

  const downloadPDF = () => {
    if (pdfBlob) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const rotatePDF = () => {
    setPdfRotation((previousRotation) => (previousRotation + PDF_ROTATION_STEP_DEGREES) % 360);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const selectedTagField = selectedTagPopup?.field || null;
  const selectedTagCoordinates = normalizeTagPopupCoordinates(selectedTagField);
  const ViewerRoot = embedded ? Box : StyledDialog;
  const viewerRootProps = embedded
    ? { sx: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' } }
    : { open, onClose, maxWidth: false, fullScreen: isFullscreen || isSmallScreen };

  return (
    <>
    <ViewerRoot {...viewerRootProps}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <PictureAsPdfIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.pdf_viewer_title')} - ${filename}` : t('viewer.pdf_viewer_title')}
          </Typography>
        </Box>
        {!embedded && <CloseButton
          onClick={onClose}
          sx={{
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        />}
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadPDF} 
            disabled={!pdfBlob}
            size="small"
            title={t('viewer.download_pdf')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton 
            onClick={rotatePDF}
            disabled={!pdfUrl}
            size="small"
            title={t('viewer.rotate_pdf')}
          >
            <Rotate90DegreesCwIcon />
          </IconButton>
          <IconButton
            onClick={() => setZoomScale((current) => Math.max(0.5, Number((current - 0.25).toFixed(2))))}
            disabled={zoomScale <= 0.5}
            size="small"
            title="Zoom out"
          >
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 42, textAlign: 'center' }}>
            {Math.round(zoomScale * 100)}%
          </Typography>
          <IconButton
            onClick={() => setZoomScale((current) => Math.min(3, Number((current + 0.25).toFixed(2))))}
            disabled={zoomScale >= 3}
            size="small"
            title="Zoom in"
          >
            <ZoomInIcon />
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_pdf')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <PDFContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_pdf')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_pdf')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : pdfUrl ? (
            <Box sx={{ width: '100%', height: '100%', p: 2, flexShrink: 0 }}>
              {pageRendering && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('viewer.rendering_pdf')} {renderedPages}/{pageCount || 0}
                  </Typography>
                  <LinearProgress
                    variant={pageCount ? 'determinate' : 'indeterminate'}
                    value={pageCount ? (renderedPages / pageCount) * 100 : undefined}
                  />
                </Box>
              )}
              {pageRenderError ? (
                <Typography color="error">
                  {pageRenderError}
                </Typography>
              ) : (
                <Box
                  ref={pagesContainerRef}
                  sx={{ width: '100%', minWidth: '100%' }}
                />
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No PDF content available
            </Typography>
          )}
        </PDFContainer>
      </ViewerDialogContent>
    </ViewerRoot>
    <Popover
      open={Boolean(selectedTagPopup?.anchorEl && selectedTagField)}
      anchorEl={selectedTagPopup?.anchorEl || null}
      onClose={() => setSelectedTagPopup(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 'min(900px, calc(100vw - 24px))',
          maxWidth: 'min(900px, calc(100vw - 24px))',
          maxHeight: 'min(84vh, 760px)',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          borderRadius: '12px',
          border: `2px solid ${primaryColor}20`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'min(84vh, 760px)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.5,
            pb: 1,
            flexShrink: 0,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            borderBottom: `2px solid ${primaryColor}15`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              color: primaryColor,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedTagField?.variable || ''}
          </Typography>
          <CloseButton
            size="small"
            onClick={() => setSelectedTagPopup(null)}
            sx={{ color: primaryColor }}
          />
        </Box>
        <Box sx={{ p: 2, overflow: 'auto' }}>
          <UnifiedAnswerRenderer
            tag={selectedTagField}
            value={selectedTagField?.reply ?? ''}
            widgetId={`pdfviewer-tag-preview-${selectedTagField?.id || selectedTagField?.variable || 'tag'}`}
            disableInternalScroll={true}
          />
          {selectedTagCoordinates.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid rgba(87, 87, 176, 0.18)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  color: primaryColor,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {t('timeevent.tag_coordinates', 'Coordinates')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {selectedTagCoordinates.map((item, index) => (
                  <Box
                    key={`${item.page}-${index}`}
                    sx={{
                      p: 1,
                      borderRadius: '8px',
                      backgroundColor: 'rgba(244, 67, 54, 0.06)',
                      border: '1px solid rgba(244, 67, 54, 0.22)',
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', color: '#333', fontWeight: 600 }}>
                      {t('timeevent.tag_coordinates_page', 'Page {{page}}', { page: item.page })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#555',
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                      }}
                    >
                      [{item.bbox.map(formatCoordinateNumber).join(', ')}]
                    </Typography>
                    {item.text && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: '#777',
                          wordBreak: 'break-word',
                        }}
                      >
                        {t('timeevent.tag_coordinates_text', 'Matched text')}: {item.text}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Popover>
    </>
  );
});

// Set display name for debugging
PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;
